import { useCallback } from 'react';
import type { GoodsFirstPageRenderingModel } from '../../../../api/types';
import { bridge } from '../../../bridge';
import { getWebBaseURL } from '../../../getWebBaseURL';
import {
  appendParams,
  buildUrl,
  finalizeUrl,
  getDefaultDeeplink,
} from '../../utils';
import { useExperimentGroup } from '../../../../context/UserContext';
import { isWeb } from '../../../isWeb';
import { useRootStackNavigation } from '../../../../hooks/useRootNavigation';
import { useInstanceId } from '../../../../hooks';
import { isNotNil } from '../../../isNotNil';
import { useAnalyticsContext } from '../../../../context/AnalyticsContext';

type MoveToGoodsProps = {
  sno: number;
  firstPageRendering?: GoodsFirstPageRenderingModel | null;
  loggingAnalytics?: Record<string, unknown>;
  couponSno?: number;
  alwaysRN?: boolean;
  remoteDeeplink?: string;
  ad?: object | null;
};

const DEBUG_MODE = __DEV__;

export const useMoveToGoods = () => {
  const isGoodsDetailRN = useExperimentGroup({
    sno: isWeb ? 'EXP1615' : `EXP1577`,
    targetGroups: ['B'],
  });

  const navigation = useRootStackNavigation();
  const instanceId = useInstanceId();
  // 상세 진입 시 출처 화면(FROM)으로 쓸 현재 화면명
  const { screen } = useAnalyticsContext();

  return useCallback(
    ({
      firstPageRendering,
      sno,
      loggingAnalytics,
      couponSno,
      alwaysRN,
      remoteDeeplink,
      ad,
    }: MoveToGoodsProps) => {
      const experimentGroup = isGoodsDetailRN || alwaysRN || DEBUG_MODE;
      const getDeeplink = () => {
        if (experimentGroup) {
          const url = getDefaultDeeplink();
          appendParams(url, 'screen', 'GoodsDetail');
          return url;
        }

        return buildUrl(`ably://goods/${sno}`);
      };
      // 출처 화면(FROM) from-context를 logging_analytics로 전달한다.
      // 검색 만족도 등 dbt 지표가 prev_screen=`$.FROM`에 의존하는데, RN→상세는
      // deeplink 경유라 출처가 없으면 네이티브가 'DEEPLINK'로 폴백되어 집계에서 누락된다.
      // ⚠️ 전체 loggingAnalytics를 넣으면 Android(GoodsDetailDom)가 putAll로 SCREEN 등
      // 검색결과 파라미터를 상세 화면 기본값으로 덮어써 버린다. 그래서 FROM(+검색어)만
      // 최소로 싣는다. (iOS ProductNavigationParams는 FROM/SEARCH_KEYWORD만 추출)
      const fromScreen =
        typeof loggingAnalytics?.FROM === 'string'
          ? loggingAnalytics.FROM
          : screen || undefined;
      const fromContext: Record<string, unknown> | undefined = fromScreen
        ? {
            FROM: fromScreen,
            // 검색 유입 시 SEARCH_KEYWORD 귀속 유지(네이티브 기존 동작 패리티)
            ...(loggingAnalytics?.SEARCH_KEYWORD != null
              ? { SEARCH_KEYWORD: loggingAnalytics.SEARCH_KEYWORD }
              : {}),
          }
        : undefined;
      const fromContextJson = fromContext
        ? JSON.stringify(fromContext)
        : undefined;

      const extras = {
        sno,
        ...(loggingAnalytics ? loggingAnalytics : {}),
        // RN 상품상세 라우트가 읽는 위치(extras.logging_analytics, JSON 문자열)
        ...(fromContextJson ? { logging_analytics: fromContextJson } : {}),
        ...(firstPageRendering ? firstPageRendering : {}),
        ...(couponSno ? { coupon_sno: couponSno } : {}),
        ...(ad ? { ad: JSON.stringify(ad) } : {}),
      };
      const goodsDetailDeeplink = getDeeplink();
      appendParams(goodsDetailDeeplink, 'extras', JSON.stringify(extras));
      // 네이티브 상품상세(ProductNavigationParams / Android GoodsDetailDom)는
      // top-level logging_analytics를 읽는다. FROM(+검색어) 최소 컨텍스트만 전달.
      if (fromContextJson) {
        appendParams(goodsDetailDeeplink, 'logging_analytics', fromContextJson);
      }

      if (isNotNil(remoteDeeplink)) {
        return bridge.openDeeplink(remoteDeeplink, {
          webCallback: async () => {
            const baseURL = await getWebBaseURL();
            location.href = `${baseURL}/${remoteDeeplink.replace('ably://webview?url=', '').replace('https://m.a-bly.com/', '')}`;
          },
        });
      }

      return bridge.openDeeplink(finalizeUrl(goodsDetailDeeplink), {
        webCallback: async () => {
          if (experimentGroup) {
            navigation.push('GoodsDetail', {
              instanceId,
              goodsId: sno,
              extras: JSON.stringify(extras),
            });
          } else {
            const baseURL = await getWebBaseURL();
            location.href = `${baseURL}/goods/${sno}`;
          }
        },
      });
    },
    [isGoodsDetailRN, navigation, instanceId, screen],
  );
};
